import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminApi from "@/lib/adminApi";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import { GOLD } from "@/lib/brand";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // "login" | "setup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // if already authenticated, go to panel
    const t = localStorage.getItem("admin_token");
    if (t) {
      adminApi.get("/admin-auth/me").then(() => navigate("/superadmin")).catch(() => localStorage.removeItem("admin_token"));
    }
    adminApi.get("/admin-auth/status").then((r) => setMode(r.data.exists ? "login" : "setup")).catch(() => setMode("login"));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "setup" ? "/admin-auth/setup" : "/admin-auth/login";
      const res = await adminApi.post(endpoint, { email, password });
      localStorage.setItem("admin_token", res.data.token);
      toast.success(mode === "setup" ? "Account admin creato" : "Accesso effettuato");
      navigate("/superadmin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "mt-1 w-full rounded-xl border border-white/15 bg-white/5 text-[#F5F1E6] px-4 py-3 placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#B2A475]/40 focus:border-[#B2A475] transition";

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center px-4 py-10 relative overflow-hidden" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: GOLD }} />
      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center justify-center gap-3 mb-8">
          <BrandLogo className="w-12 h-12" />
          <span className="text-[#F5F1E6] text-2xl tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>The Cube Design</span>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 shadow-2xl">
          <div className="flex items-center gap-2 text-[#B2A475]"><ShieldCheck className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.2em] font-semibold">Pannello Amministratore</span></div>
          <h1 className="text-3xl gold-text mt-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{mode === "setup" ? "Crea account admin" : "Accesso admin"}</h1>
          <p className="text-white/50 text-sm mt-1">{mode === "setup" ? "Primo accesso: scegli le tue credenziali admin." : "Gestione piattaforma e abbonamenti."}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-white/70">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-white/70">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" className={inputCls} />
            </div>
            <button type="submit" disabled={loading || !mode} data-testid="admin-submit" className="w-full rounded-full font-semibold py-3 transition-transform hover:scale-[1.01] flex items-center justify-center gap-2" style={{ background: GOLD, color: "#0B0B0C" }}>
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} {mode === "setup" ? "Crea account admin" : "Accedi"}
            </button>
          </form>
          <p className="text-white/45 text-xs mt-5 text-center leading-relaxed">
            Questo è l'accesso riservato all'<b>amministratore della piattaforma</b>.<br />
            Sei un <b>ristoratore</b>?{" "}
            <button type="button" onClick={() => navigate("/login")} data-testid="go-owner-login" className="text-[#B2A475] font-semibold hover:underline">Accedi al tuo pannello qui</button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
